/**
 * @module store/modules/supervisor/getters
 * @description Supervisor Vuex getters for computed state values.
 */

import { GetterTree } from "vuex/types";
import { RootState } from "@/store/types";
import { SupervisorState } from "./types";
import _ from "lodash";

const getters: GetterTree<SupervisorState, RootState> = {
  /**
   * Gets the count of online agents.
   *
   * @param {SupervisorState} _state - Current module state
   * @param {Object} getters - Module getters
   * @returns {number} Number of online agents
   */
  onlineAgentsCount(_state, getters, _rootState, _rootGetters) {
    return getters.onlineAgentsWithChatCount?.length;
  },
  /**
   * Gets the count of offline agents.
   *
   * @param {SupervisorState} _state - Current module state
   * @param {Object} getters - Module getters
   * @returns {number} Number of offline agents
   */
  offlineAgentsCount(_state, getters, _rootState, _rootGetters) {
    return getters.offlineAgentsWithChatCount?.length;
  },
  /**
   * Gets the total count of all chats across all categories.
   *
   * @param {SupervisorState} state - Current module state
   * @param {Object} getters - Module getters
   * @returns {number} Total number of chats
   */
  totalChatsCount(state, getters, _rootState, rootGetters) {
    return (
      state.inQueueChatCount +
      state.onGoingChatCount +
      state.abandonedChatCount +
      state.resolvedChatCount
    );
  },
  /**
   * Checks if online agents view is active.
   *
   * @param {SupervisorState} state - Current module state
   * @returns {boolean} True if showing online agents
   */
  showOnlineAgents(state, _getters, _rootState) {
    return state.currentMode === "ONLINE_AGENTS";
  },
  /**
   * Checks if offline agents view is active.
   *
   * @param {SupervisorState} state - Current module state
   * @returns {boolean} True if showing offline agents
   */
  showOfflineAgents(state, _getters, _rootState) {
    return state.currentMode === "OFFLINE_AGENTS";
  },
  /**
   * Gets the currently selected agent email.
   *
   * @param {SupervisorState} state - Current module state
   * @returns {string|null} Selected agent email
   */
  selectedAgent(state, _getters, _rootState) {
    return state.selectedAgent;
  },
  /**
   * Gets the currently selected chat session.
   *
   * @param {SupervisorState} state - Current module state
   * @returns {Object|null} Selected chat session
   */
  selectedChatSession(state, _getters, _rootState) {
    return state.selectedChatSession;
  },
  /**
   * Gets the selected department filter.
   *
   * @param {SupervisorState} state - Current module state
   * @returns {string[]|undefined} Array of selected departments
   */
  selectedDepartmentFilter(state, _getters, _rootState) {
    return state.selectedDepartmentFilter;
  },
  /**
   * Gets all agents with their chat counts.
   *
   * @param {SupervisorState} state - Current module state
   * @returns {Array} Array of agents with chat counts
   */
  agentsWithChatCount(state, _getters, _rootState) {
    return state.agentsWithChatCount;
  },
  /**
   * Gets only online agents with their chat counts.
   *
   * @param {SupervisorState} _state - Current module state
   * @param {Object} getters - Module getters
   * @returns {Array} Filtered array of online agents
   */
  onlineAgentsWithChatCount(_state, getters, _rootState) {
    return _.filter(getters.agentsWithChatCount, ({ agent }) => agent.livechat_status === "ONLINE");
  },
  /**
   * Gets only offline agents with their chat counts.
   *
   * @param {SupervisorState} _state - Current module state
   * @param {Object} getters - Module getters
   * @returns {Array} Filtered array of offline agents
   */
  offlineAgentsWithChatCount(_state, getters, _rootState) {
    return _.filter(getters.agentsWithChatCount, ({ agent }) => agent.livechat_status !== "ONLINE");
  },
};

export default getters;
